/**
 * Media feature - Figma `6377:87404` (and `6377:87489`).
 *
 * The same two-column head as `stats` - a large heading on the left, a paragraph on the
 * right - above a row of supporting photographs indented from the left margin.
 *
 * **The content model was wrong here.** The spec described "a single supporting photo"
 * and gave the section one `shared.media`; the design shows two side by side. The field
 * is now repeatable, which covers one photograph as well as several, and keeps the whole
 * `shared.media` component per image so each one still carries its own alt text.
 *
 * The mock-up's two images have different widths, which follows from their own aspect
 * ratios rather than from a layout rule. Equal columns are the neutral reading; a
 * designer who meant a fixed 1:2 split should say so.
 *
 * **`customer-care-intro` and `tool-cta` were merged into this on 2026-09-04.** All three
 * drew the same block - the two-column head, a call to action, and a row underneath - and
 * differed only in what the row held. So the row of grey cards `customer-care-intro` drew
 * is `cards` here, and `tool-cta`, which was the head over a full-width band, is this
 * section followed by a `hero-media`: which is how both tool pages are already built from
 * their Final-UI frames.
 *
 * Cards come before photographs when an editor fills in both, because that is the order
 * the file stacks them in on Opieka klienta.
 */
import { Blocks, CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import type { MediaFeatureSection } from '../../types/sections';
import { SIZES } from '../../components/ui/Image';
import { MediaFrame, aspectClass, hasMedia } from '../../components/ui/MediaFrame';

export default function MediaFeature({
  data, first = false
}: { data: MediaFeatureSection; first?: boolean }) {
  const images = (data.media ?? []).filter(hasMedia);
  const cards = data.cards ?? [];
  // This block opens the service pages (6377:87404), where its title is the page title
  // at Display-01 rather than a heading inside one. Anywhere else it is an h2.
  const Title = first ? 'h1' : 'h2';

  return (
    <SectionShell settings={data.settings} name="media-feature">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {/* 40px between the columns, not 64. Measured on 3883:71066: the head is two equal
          halves of (1360 - 40), and at 64 the title column loses 12px - enough to push
          an 82px Display line over and make the block a whole line taller. */}
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-10">
        <Title className={`text-h2 text-ink ${first ? 'lg:text-display' : 'lg:text-h1'}`}>{withHighlight(data.heading)}</Title>
        {/* body-medium (18px) in the file, not body. On the prose element itself: set on
            a wrapper, `.weco-prose`'s own `text-body` beats inheritance and it renders 16px. */}
        {data.body ? <Blocks value={data.body} className="text-body-md" /> : null}
      </div>

      {data.cta ? <div className="pt-2"><CtaLink value={data.cta} /></div> : null}

      {cards.length ? (
        // 6377:87479 - two grey cards under the head, each a title and a paragraph.
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {cards.map((card, i) => (
            <article key={i} className="flex flex-col gap-6 rounded-s bg-surface-muted p-6">
              <h3 className="text-h5 text-ink-strong">{card.title}</h3>
              {card.body ? <p className="text-body text-ink-muted">{card.body}</p> : null}
            </article>
          ))}
        </div>
      ) : null}

      {images.length ? (
        // Indented from the left margin, as in 6377:87404.
        // The pair is not two equal halves: the file draws a 376px square beside a 669px
        // landscape (3883:87889), which is why the row is indented rather than centred.
        // Three or more fall back to equal columns - the design only ever shows two.
        // A single photograph is not an indented row of one: 3883:71071 puts it in the
        // right half of the grid, which is where the two-image row's second image would
        // have been.
        //
        // **A pair is one height** (6377:87410: a 376 square beside a 669x376 landscape). The
        // first photograph keeps its own shape and sets the row; the second has none of its
        // own beside it and fills that height at its width. Both are laid in with `absolute
        // inset-0`, or the second picture's own proportion would stretch the row past the first.
        <div className={`mt-8 grid gap-4 ${
          images.length === 1 ? 'lg:ml-[50%]'
          : images.length === 2 ? 'sm:grid-cols-2 lg:ml-[22%] lg:grid-cols-[376fr_669fr]'
          : 'sm:grid-cols-2 lg:ml-[22%]'
        }`}>
          {images.map((item, i) => {
            const follower = images.length === 2 && i === 1;
            return (
              <figure
                key={i}
                className={`relative m-0 overflow-hidden rounded-l bg-surface-sunken ${
                  follower ? 'aspect-[16/9] sm:aspect-auto'
                  : aspectClass(item) ?? (images.length === 2 ? 'aspect-square' : 'aspect-[4/3]')
                }`}
              >
                <MediaFrame
                  media={item}
                  sizes={SIZES.half}
                  title={data.heading ?? undefined}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </figure>
            );
          })}
        </div>
      ) : null}
    </SectionShell>
  );
}
